import { MockServiceStore } from "./MockServiceStore";
import { mockPurchaseOrders } from "./purchase-orders.mock";

/**
 * Line item status
 */
export type LineItemStatus = "ordered" | "partial" | "received" | "cancelled";

/**
 * Purchase Order Line Item entity type
 */
export interface PurchaseOrderLineItem {
  lineItemId: number;
  purchaseOrderId: number;
  lineNumber: number;
  itemCode?: string;
  description: string;
  specifications?: string;
  quantity: number;
  receivedQuantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  extendedPrice: number;
  glAccountId: number;
  glAccountNumber?: string;
  expectedDeliveryDate?: string;
  actualDeliveryDate?: string;
  status: LineItemStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Sample item codes and descriptions
 */
const sampleItems = [
  { code: "RBR-4", desc: "Rebar #4", uom: "LB", priceRange: [0.5, 1.5] },
  { code: "RBR-5", desc: "Rebar #5", uom: "LB", priceRange: [0.6, 1.8] },
  { code: "CONC-MIX", desc: "Concrete Mix", uom: "CY", priceRange: [100, 200] },
  { code: "AGG-3/4", desc: "Aggregate 3/4 inch", uom: "TON", priceRange: [25, 50] },
  { code: "FORM-PLY", desc: "Form Plywood", uom: "SHEET", priceRange: [40, 80] },
  { code: "FORM-OIL", desc: "Form Release Oil", uom: "GAL", priceRange: [15, 30] },
  { code: "SEAL-CURE", desc: "Curing Compound", uom: "GAL", priceRange: [20, 45] },
  { code: "EMBED-A", desc: "Embed Anchor Plate", uom: "EA", priceRange: [50, 150] },
  { code: "EMBED-L", desc: "Embed Lift Insert", uom: "EA", priceRange: [8, 25] },
  { code: "MESH-6X6", desc: "Wire Mesh 6x6", uom: "ROLL", priceRange: [150, 300] },
  { code: "GROUT-NS", desc: "Non-Shrink Grout", uom: "BAG", priceRange: [30, 60] },
  { code: "MISC", desc: "Miscellaneous Materials", uom: "EA", priceRange: [0, 0] }, // $0 item
];

/**
 * GL Account numbers
 */
const glAccounts = [
  { id: 1, number: "5000-100" },
  { id: 2, number: "5000-200" },
  { id: 3, number: "5000-300" },
  { id: 4, number: "5100-100" },
  { id: 5, number: "5100-200" },
];

/**
 * Generate mock line items for each PO
 */
const generateMockLineItems = (): PurchaseOrderLineItem[] => {
  const lineItems: PurchaseOrderLineItem[] = [];
  let lineItemId = 1;

  mockPurchaseOrders.forEach((po) => {
    const numItems = Math.floor(Math.random() * 5) + 2; // 2-6 items per PO

    for (let i = 1; i <= numItems; i++) {
      const item = sampleItems[Math.floor(Math.random() * sampleItems.length)];
      const glAccount = glAccounts[Math.floor(Math.random() * glAccounts.length)];
      const quantity = Math.floor(Math.random() * 100) + 1;
      const unitPrice =
        item.priceRange[0] === 0 && item.priceRange[1] === 0
          ? 0 // $0 item support
          : Math.round(
              (Math.random() * (item.priceRange[1] - item.priceRange[0]) + item.priceRange[0]) * 100
            ) / 100;
      const extendedPrice = Math.round(quantity * unitPrice * 100) / 100;

      // Determine status based on PO status
      let status: LineItemStatus = "ordered";
      let receivedQuantity = 0;

      if (["received", "pending_approval", "approved", "paid"].includes(po.status)) {
        status = "received";
        receivedQuantity = quantity;
      } else if (po.status === "partially_received") {
        if (Math.random() > 0.5) {
          status = "received";
          receivedQuantity = quantity;
        } else {
          status = "partial";
          receivedQuantity = Math.floor(quantity * 0.5);
        }
      } else if (po.status === "cancelled") {
        status = "cancelled";
      }

      lineItems.push({
        lineItemId: lineItemId++,
        purchaseOrderId: po.purchaseOrderId,
        lineNumber: i,
        itemCode: item.code,
        description: item.desc,
        specifications: Math.random() > 0.7 ? "Per spec sheet attached" : undefined,
        quantity,
        receivedQuantity,
        unitOfMeasure: item.uom,
        unitPrice,
        extendedPrice,
        glAccountId: glAccount.id,
        glAccountNumber: glAccount.number,
        expectedDeliveryDate: po.expectedDeliveryDate,
        actualDeliveryDate: po.actualDeliveryDate,
        status,
        notes: Math.random() > 0.8 ? "Standard item" : undefined,
        createdAt: po.createdAt,
        updatedAt: new Date().toISOString(),
      });
    }
  });

  return lineItems;
};

/**
 * Mock purchase order line items data
 */
export const mockPurchaseOrderLineItems: PurchaseOrderLineItem[] = generateMockLineItems();

/**
 * Create mock purchase order line items store
 */
export const createMockPurchaseOrderLineItemsStore = () =>
  new MockServiceStore<PurchaseOrderLineItem>({
    mockData: mockPurchaseOrderLineItems,
    idField: "lineItemId",
    simulateDelay: 250,
  });

export default mockPurchaseOrderLineItems;
